/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

// This class allows integration with OCAPI Orders Resource
// https://documentation.b2c.commercecloud.salesforce.com/DOC2/topic/com.demandware.dochelp/OCAPI/current/shop/Resources/Orders.html
// This implementations coverts CAPI requests to OCAPI requests as there are fundamental differences between the APIS
// One major difference is OCAPI uses snake_case and CAPI uses camelCase for this reaso you will see a utility function in here that convert
// from camelCase to snake_case - camelCaseKeysToUnderscore
// createOcapiFetch is another utility function that returns the response from OCAPI in the fromat returned from CAPI
// Another utility function - checkRequiredParameters is used to check if the parameters or body objects necessary for a call are
// present in the request before making it

import {camelCaseKeysToUnderscore, checkRequiredParameters, createOcapiFetch} from './utils'

class OcapiShopperOrders {
    constructor(config) {
        this.fetch = createOcapiFetch(config)
    }

    async createOrder(...args) {
        const required = ['body']
        let requiredParametersError = checkRequiredParameters(args[0], required)
        if (requiredParametersError) {
            return requiredParametersError
        }

        const {body} = args[0]
        return await this.fetch(
            `orders?token=${body.basketId}`,
            'POST',
            args,
            'createOrder',
            camelCaseKeysToUnderscore(body)
        )
    }

    async getOrder(...args) {
        const required = ['orderNo']
        let requiredParametersError = checkRequiredParameters(args[0], required)
        if (requiredParametersError) {
            return requiredParametersError
        }
        const {orderNo} = args[0].parameters
        return this.fetch(`orders/${orderNo}`, 'GET', args, 'getOrder')
    }

    async failOrder(...args) {
        const {orderNo, merchantReference, _resource_state} = args[0].body
        const reason = args[0].reason
        const body = {}
        body.status = 'failed'
        // keep the merchant reference just for traceability
        body.c_Adyen_merchantReference = 'failed-order-reference-' + merchantReference
        body._resource_state = _resource_state
        return this.fetch(`orders/${orderNo}?reason=${reason}`, 'PATCH', args, 'failOrder', body)
    }

    async cancelOrder(...args) {
        const {orderNo, merchantReference, _resource_state} = args[0].body
        const body = {}
        // keep the merchant reference just for traceability
        body.c_Adyen_merchantReference = 'failed-order-reference-' + merchantReference
        body._resource_state = _resource_state
        return this.fetch(`orders/${orderNo}?status=cancelled`, 'PATCH', args, 'cancelOrder', body)
    }

    async updateAdyenReference(...args) {
        const {orderNo, _resource_state} = args[0].body
        const body = {}
        // reference used to connect Adyen transaction and SFCC order
        body.c_Adyen_merchantReference = args[0].adyenReference
        body.c_Adyen_paymentMethod = args[0].paymentMethod
        body.c_Adyen_paymentData = args[0].paymentData
        body.c_cancellationEndTimeInUserTz = args[0].timeZone
        body._resource_state = _resource_state
        return this.fetch(`orders/${orderNo}`, 'PATCH', args, 'updateAdyenReference', body)
    }

    async updateAdyenData(...args) {
        const {orderNo, _resource_state} = args[0].body
        const body = {}
        body.c_adyenSessionData = args[0].sessionData
        body._resource_state = _resource_state
        return this.fetch(`orders/${orderNo}`, 'PATCH', args, 'updateAdyenData', body)
    }

    async updateCustomerData(...args) {
        const required = ['orderNo', 'customerNo']
        let requiredParametersError = checkRequiredParameters(args[0], required)
        if (requiredParametersError) {
            return requiredParametersError
        }
        const {orderNo, customerNo} = args[0].parameters
        return this.fetch(
            `orders/${orderNo}?customer=${customerNo}`,
            'PATCH',
            args,
            'updateCustomerData',
            {}
        )
    }
}

export default OcapiShopperOrders
